import { useMemo } from "react";
import { CACHE_TIMES } from "../../../query/options";
import type { SubscriptionRow } from "../../../utils/zod/subscription";
import { useInfiniteSubscriptions } from "./useInfiniteSubscriptions";

export interface ExpiringClient {
  publicId: string;
  firstName: string;
  lastName?: string | null;
  businessName?: string | null;
  planName: string;
  endDate: string;
  daysRemaining: number;
  daysExpired: number;
}

const subscriptionToExpiringClient = (
  row: SubscriptionRow,
): ExpiringClient => ({
  publicId: row.publicId,
  firstName: row.client.firstName,
  lastName: row.client.lastName,
  businessName: row.client.businessName,
  planName: row.plan.name,
  endDate: row.endDate,
  daysRemaining: row.remainingDays > 0 ? row.remainingDays : 0,
  daysExpired: row.remainingDays < 0 ? Math.abs(row.remainingDays) : 0,
});

interface UseExpiringClientsOptions {
  enabled?: boolean;
}

const PAGE_SIZE = 15;
export const useExpiringClients = (options: UseExpiringClientsOptions = {}) => {
  const { enabled = true } = options;

  const expiringSoonQuery = useInfiniteSubscriptions(
    {
      expiringInDays: 3,
      filterStatus: "ACTIVE",
      sort: "DAYS_ASC",
      pageSize: PAGE_SIZE,
    },
    { enabled, ...CACHE_TIMES.TRANSACTIONAL_DATA },
  );

  const longExpiredQuery = useInfiniteSubscriptions(
    {
      expiredSinceDays: 7,
      filterStatus: "EXPIRED",
      sort: "DAYS_DESC",
      pageSize: PAGE_SIZE,
    },
    { enabled, ...CACHE_TIMES.TRANSACTIONAL_DATA },
  );

  const expiringSoon = useMemo(
    () =>
      expiringSoonQuery.data?.pages.flatMap((page) =>
        page.data.map(subscriptionToExpiringClient),
      ) ?? [],
    [expiringSoonQuery.data],
  );

  const longExpired = useMemo(
    () =>
      longExpiredQuery.data?.pages.flatMap((page) =>
        page.data.map(subscriptionToExpiringClient),
      ) ?? [],
    [longExpiredQuery.data],
  );

  return {
    expiringSoon,
    longExpired,
    isLoading: expiringSoonQuery.isLoading || longExpiredQuery.isLoading,
    isFetchingNextPage:
      expiringSoonQuery.isFetchingNextPage ||
      longExpiredQuery.isFetchingNextPage,
    expiringSoonQuery,
    longExpiredQuery,
  };
};
